import { formaterJour, formaterPeriode, promptTacheClaude } from '@admin-pmvb/core'
import { useQuery, useQueryClient } from '@tanstack/react-query'
import { Link } from '@tanstack/react-router'
import { useState, type ReactNode } from 'react'
import { api, ErreurApi, ecrit, lire, requeteMoi, type Tache, type VueTaches } from '../api'
import { initiales, jourCourt, LIBELLES_STATUT_TACHE } from '../libelles'
import { Icone, type NomIcone } from './Icone'
import { BoutonMail } from './ApercuPiece'
import { BoutonClaude } from './BoutonClaude'
import { feterTache } from './confettis'
import { confirmer } from './confirmation'
import { LogoConnu } from './Logo'

// Ligne de tâche partagée (Tâches, accueil, fiche d'échéance) : état, affectation,
// « je prends » et changements de statut. Terminer une tâche qui porte un paiement
// propose de le marquer payé : jamais coché sans l'accord de la personne.

export type EvenementTache =
  'demarrer' | 'attendre_tiers' | 'reprendre' | 'terminer' | 'rouvrir' | 'annuler'

const ACTIONS: Record<Tache['statut'], { evenement: EvenementTache; libelle: string }[]> = {
  a_faire: [
    { evenement: 'demarrer', libelle: 'Démarrer' },
    { evenement: 'terminer', libelle: 'Faite' },
  ],
  en_cours: [
    { evenement: 'attendre_tiers', libelle: 'En attente d’un tiers' },
    { evenement: 'terminer', libelle: 'Faite' },
  ],
  en_attente_tiers: [
    { evenement: 'reprendre', libelle: 'Reprendre' },
    { evenement: 'terminer', libelle: 'Faite' },
  ],
  faite: [{ evenement: 'rouvrir', libelle: 'Rouvrir' }],
  annulee: [{ evenement: 'rouvrir', libelle: 'Rouvrir' }],
}

/** Actions sur une tâche, avec la proposition de marquer payé après « faite ». */
export function useActionsTache() {
  const client = useQueryClient()
  const [erreur, setErreur] = useState<string | null>(null)

  /** Vrai si l'action a abouti. */
  async function agir(action: () => Promise<unknown>): Promise<boolean> {
    setErreur(null)
    try {
      await action()
      return true
    } catch (e) {
      setErreur(e instanceof ErreurApi ? e.message : 'Action impossible.')
      return false
    } finally {
      await client.invalidateQueries()
    }
  }

  return {
    erreur,
    prendre: (id: string) => agir(() => lire(api.taches[':id'].prendre.$post({ param: { id } }))),
    changer: (id: string, evenement: EvenementTache) =>
      agir(async () => {
        const r = await lire(api.taches[':id'].statut.$post({ param: { id }, json: { evenement } }))
        const aPayer =
          r.proposition &&
          (await confirmer('Cette tâche porte un paiement. Le marquer payé maintenant ?'))
            ? r.proposition
            : null
        // Après la question : sa fenêtre passerait devant les confettis.
        if (evenement === 'terminer') feterTache(id)
        if (aPayer) {
          await lire(
            api.paiements['marquer-payes'].$post({
              json: { paiementIds: [aPayer.paiementId] },
            }),
          )
        }
      }),
  }
}

export const ICONES_STATUT_TACHE: Record<Tache['statut'], NomIcone> = {
  a_faire: 'radio_button_unchecked',
  en_cours: 'clock_loader_40',
  en_attente_tiers: 'hourglass_top',
  faite: 'check_circle',
  annulee: 'cancel',
}

export function BadgesTache({ t, age = false }: { t: Tache; age?: boolean }) {
  return (
    <>
      {t.nouvelle && (
        <span className="badge plein-accent" title="Confiée à vous, pas encore ouverte">
          Nouvelle
        </span>
      )}
      {t.declaration && (
        <span className="badge badge-etat" title="Déclaration ou paiement dû à l’État">
          <Icone nom="account_balance" /> État
        </span>
      )}
      {t.urgente && <span className="badge ton-alerte">Urgente</span>}
      {t.enRetard && <span className="badge ton-alerte">En retard</span>}
      {t.aPrendre && <span className="badge ton-ok">À prendre</span>}
      {t.type === 'decision' && <span className="badge">Décision</span>}
      {t.endormie && t.reporteeAu && (
        <span className="badge" title="Absente des listes jusqu’à ce jour">
          <Icone nom="snooze" /> Revient le {formaterJour(t.reporteeAu)}
        </span>
      )}
      {t.reporteePeriode && ['a_faire', 'en_cours', 'en_attente_tiers'].includes(t.statut) && (
        <span className="badge" title={t.reporteeEcheance ?? undefined}>
          Pour le bilan {formaterPeriode(t.reporteePeriode)}
        </span>
      )}
      {age && t.attenteJours !== null && (
        <span className="badge mono ton-ambre">depuis {t.attenteJours} j</span>
      )}
    </>
  )
}

/** Qui fait, ou qui peut la prendre ; pour un tiers, qui suit. */
export function affectationDe(t: Tache): string {
  const garants = t.garants.map((g) => g.nom?.split(' ')[0]).join(', ')
  if (t.executant) return t.executant
  if (t.executantTiers) return `${t.executantTiers}, suivi par ${garants}`
  const ouverte = ['a_faire', 'en_cours', 'en_attente_tiers'].includes(t.statut)
  return ouverte ? `à prendre : ${garants}` : `sans exécutant désigné (${garants})`
}

/**
 * `vue` : la liste d'où l'on vient, pour parcourir ses tâches une à une depuis la fiche ;
 * `sansStatut` : sans les boutons de statut (la fiche a sa propre barre).
 */
export function LigneTache({
  t,
  sansEntite = false,
  sansStatut = false,
  vue,
  dansProjet = false,
  actionsEnPlus,
}: {
  t: Tache
  sansEntite?: boolean
  sansStatut?: boolean
  vue?: VueTaches
  /** Listée dans son projet : le lien vers le projet est inutile. */
  dansProjet?: boolean
  /** Actions propres à la liste (projet : montrer dans les tâches, retirer). */
  actionsEnPlus?: ReactNode
}) {
  const moi = useQuery(requeteMoi).data
  const { erreur, prendre, changer } = useActionsTache()
  const ecriture =
    moi !== undefined && (t.entiteId === null ? moi.administrateur : ecrit(moi, t.entiteId))
  const ouverte =
    t.statut === 'a_faire' || t.statut === 'en_cours' || t.statut === 'en_attente_tiers'
  const meta = [
    sansEntite ? null : (t.entite ?? 'Sans entité'),
    t.dateDue ? jourCourt(t.dateDue) : null,
    LIBELLES_STATUT_TACHE[t.statut],
    affectationDe(t),
  ].filter(Boolean)

  return (
    <div
      className={`ligne ligne-tache ${t.pourMoi ? 'pour-moi' : ''} ${t.declaration ? 'declaration' : ''}`}
    >
      <Icone nom={ICONES_STATUT_TACHE[t.statut]} className="ligne-icone" />
      <div className="ligne-contenu">
        <div className="ligne-titre">
          <span className="titre-logo">
            <LogoConnu nom={t.titre} taille={24} />
            <Link
              to="/taches/$id"
              params={{ id: t.id }}
              search={vue ? { vue } : {}}
              className="lien-tache"
            >
              <strong>{t.titre}</strong>
            </Link>
          </span>
          <BadgesTache t={t} age />
        </div>
        <div className="discret">
          {meta.join(' · ')}
          {t.echeanceId && (
            <>
              {' · '}
              <Link to="/echeances/$id" params={{ id: t.echeanceId }}>
                échéance
              </Link>
            </>
          )}
          {t.transactionId && (
            <>
              {' · '}
              <Link to="/rapprochements/$id" params={{ id: t.transactionId }}>
                trancher
              </Link>
            </>
          )}
          {t.projetId && !dansProjet && (
            <>
              {' · '}
              <Link to="/projets/$id" params={{ id: t.projetId }}>
                projet {t.projet}
              </Link>
            </>
          )}
        </div>
        {erreur && <p className="alerte">{erreur}</p>}
      </div>
      {(ecriture || t.lienMail || actionsEnPlus) && (
        <div className="ligne-actions">
          {ecriture && t.courrielPropose && (
            <Link
              to="/taches/$id"
              params={{ id: t.id }}
              search={vue ? { vue } : {}}
              className="bouton-icone cadre"
              title={`Courriel proposé : ${t.courrielPropose.libelle}`}
              aria-label={`Courriel proposé : ${t.courrielPropose.libelle}`}
            >
              <Icone nom="mail" taille={18} />
            </Link>
          )}
          {ecriture && ouverte && (
            <BoutonClaude
              taille="icone"
              prompt={promptTacheClaude(
                {
                  id: t.id,
                  titre: t.titre,
                  action: t.action,
                  type: t.type,
                  entite: t.entite,
                  description: t.description,
                  typeGuide: t.typeGuide,
                  avecMail: Boolean(t.lienMail),
                },
                `${window.location.origin}/taches/${t.id}`,
              )}
            />
          )}
          {t.lienMail && <BoutonMail lien={t.lienMail} discret />}
          {ecriture && t.aPrendre && (
            <button type="button" className="bouton" onClick={() => prendre(t.id)}>
              Je prends
            </button>
          )}
          {ecriture &&
            !sansStatut &&
            t.type !== 'decision' &&
            !t.aPrendre &&
            ACTIONS[t.statut].map((a) => (
              <button
                key={a.evenement}
                type="button"
                className="bouton-discret"
                onClick={() => changer(t.id, a.evenement)}
              >
                {a.libelle}
              </button>
            ))}
          {actionsEnPlus}
        </div>
      )}
    </div>
  )
}

/** Carte de tâche du tableau : entité, badges, titre, date et exécutant. */
export function CarteTache({ t, vue }: { t: Tache; vue?: VueTaches }) {
  const qui = t.executant ?? t.executantTiers
  return (
    <Link
      to="/taches/$id"
      params={{ id: t.id }}
      search={vue ? { vue } : {}}
      className={`carte-tache ${t.declaration ? 'declaration' : ''}`}
    >
      <div className="carte-tache-badges">
        <span className="badge">{t.entite ?? 'Sans entité'}</span>
        <BadgesTache t={t} />
      </div>
      <span className="titre-logo">
        <LogoConnu nom={t.titre} taille={24} />
        <strong>{t.titre}</strong>
      </span>
      <div className="carte-tache-pied">
        <span>
          <Icone nom="event" />
          {t.dateDue ? jourCourt(t.dateDue) : 'sans date'}
        </span>
        <span className="avatar petit" title={affectationDe(t)}>
          {qui ? initiales(qui).slice(0, 3) : '?'}
        </span>
      </div>
    </Link>
  )
}
